import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams } from 'react-router-dom';

import Payment from '../../../components/Payment';
import ContactInformation from '../../../components/ContactInformation';
import Credentials from '../../../components/Credentials';
import StyledTextButton from '../../../components/StyledTextButton';

import phoneImg from '../../../assets/images/phone.svg';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import LoadingButton from '@mui/lab/LoadingButton';

import { styled } from '@mui/material';

const steps = [
	'Credentials',
	'Contact Information',
	'Payment Information',
	'Confirmation'
];

const StyledListItem = styled('li')(() => ({
	margin: '15px 0',
	fontSize: '16px'
}));

const Home = () => {
	const [activeStep, setActiveStep] = React.useState(0);
	const [searchParams] = useSearchParams();
	const [openContactDialog, setOpenContactDialog] = React.useState(false);
	const [uuid, setUuid] = React.useState(null);
	const [sourceValue] = React.useState(searchParams.get('source') || '43094');
	const [skuValue] = React.useState(searchParams.get('sku') || '103596');
	const [ipAddress, setIpAddress] = React.useState(null);
	const [isLoading, setIsLoading] = React.useState(false);

	// set value if js is enabled
	const [isJavaScriptEnabled, setIsJavaScriptEnabled] = React.useState(false);
	React.useEffect(() => {
		setIsJavaScriptEnabled(true);
	}, []);

	// setting the session id as the uuid
	React.useEffect(() => {
		const sessionUuid = sessionStorage.getItem('uuid');
		if (sessionUuid) {
			setUuid(sessionUuid);
		} else {
			const newUuid = uuidv4();
			sessionStorage.setItem('uuid', newUuid);
			setUuid(newUuid);
		}
	}, []);

	// getting the ip address
	React.useEffect(() => {
		fetch('https://geolocation-db.com/json/')
			.then((response) => response.json())
			.then((data) => {
				setIpAddress(data.IPv4);
			})
			.catch((error) => {
				console.error('Error fetching IP address:', error);
			});
	}, []);

	const handleNext = () => {
		if (activeStep < steps.length - 1) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		} else {
			setActiveStep(0);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleCompleted = () => {
		setIsLoading(true);
		sessionStorage.removeItem('uuid');
		window.location.href = 'https://rizzbates.com';
	};

	const handleContactDialog = () => {
		setOpenContactDialog(true);
	};

	return (
		<Box
			sx={{
				width: '398px',
				padding: 2,
				borderRadius: '15px',
				backgroundColor: (theme) => theme.palette.primary.main,
				textAlign: 'center',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<React.Fragment>
				<Box
					sx={{
						padding: '15px'
					}}
				>
					{activeStep === 0 && (
						<Credentials
							handleNext={handleNext}
							uuid={uuid}
							sku={skuValue}
							source={sourceValue}
							ipAddress={ipAddress}
							isJavaScriptEnabled={isJavaScriptEnabled}
							timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
						/>
					)}
					{activeStep === 1 && (
						<ContactInformation handleNext={handleNext} uuid={uuid} />
					)}
					{activeStep === 2 && <Payment handleNext={handleNext} uuid={uuid} />}
					{activeStep === 3 && (
						<>
							<Box sx={{ textAlign: 'left' }}>
								<Typography variant='h5' sx={{ fontWeight: 'bold' }}>
									What&rsquo;s next?
								</Typography>
								<ul style={{ padding: '0 30px', margin: 0 }}>
									<StyledListItem>
										We will send a confirmation email and information about your
										account
									</StyledListItem>
									<StyledListItem>
										The payment will be processed after your payment is
										confirmed
									</StyledListItem>
									<StyledListItem>
										Please feel free to{' '}
										<StyledTextButton
											onClick={handleContactDialog}
											variant='text'
											fontSize='inherit'
											sx={{ verticalAlign: 'inherit' }}
										>
											contact us
										</StyledTextButton>{' '}
										if you have any questions
									</StyledListItem>
								</ul>
							</Box>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									my: 2
								}}
							>
								<LoadingButton
									variant='outlined'
									color='primary'
									onClick={handleCompleted}
									loading={isLoading}
								>
									Next
								</LoadingButton>
							</Box>
						</>
					)}
				</Box>
				<Box
					marginTop='auto'
					height='32px'
					display='flex'
					alignItems='start'
					color={(theme) => theme.palette.common.gray}
					justifyContent={
						activeStep < 2 || activeStep === steps.length - 1
							? 'flex-end'
							: 'space-between'
					}
				>
					{activeStep > 1 && activeStep < steps.length - 1 && (
						<StyledTextButton
							onClick={handleBack}
							my={3}
							variant='text'
							fontSize='18px'
						>
							{'< Back'}
						</StyledTextButton>
					)}
					<Typography
						color={(theme) => theme.palette.common.gray}
						variant='span'
						display='inline'
						width='30px'
						fontSize='18px'
					>
						{`${activeStep + 1}/${steps.length}`}
					</Typography>
				</Box>
			</React.Fragment>
			<Dialog
				fullWidth
				maxWidth='xs'
				onClose={() => setOpenContactDialog(false)}
				aria-labelledby='subscription-services'
				open={openContactDialog}
			>
				<DialogContent>
					<img src={phoneImg} alt='a hand holding a phone' />
					<Typography variant='h5' fontWeight='bold' id='subscription-services'>
						Call Subscription Services
					</Typography>
					<p>Monday to Friday, 7am - 10pm CT</p>
					<p>1-888-249-4544</p>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default Home;
